import React, { useState } from "react";
import { Box, Popover } from "@mui/material";
import { PICKER_COLORS } from "../../constants/pickerColors";

/**
 * ColourPicker - A reusable color picker component with dropdown
 *
 * @param {string} selectedColor - Currently selected color value
 * @param {function} onColorSelect - Callback when a color is selected
 * @param {string[]} disabledColors - Array of color values to disable (e.g., ["#ffa600"] to disable yellow)
 * @param {Array} colors - Optional array of color objects { name, value }. Defaults to PICKER_COLORS
 * @param {number} size - Size of the color picker button in pixels. Defaults to 40
 */
const ColourPicker = ({
  selectedColor,
  onColorSelect,
  disabledColors = [],
  colors = PICKER_COLORS,
  size = 40,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const availableColors = colors.filter(
    (color) => !disabledColors.includes(color.value),
  );

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (color) => {
    onColorSelect(color);
    handleClose();
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          bgcolor: selectedColor,
          cursor: "pointer",
          border: "2px solid",
          borderColor: "divider",
          flexShrink: 0,
          "&:hover": { opacity: 0.8 },
        }}
        title="Click to change color"
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 1,
            p: 1.5,
          }}
        >
          {availableColors.map((color) => (
            <Box
              key={color.value}
              onClick={() => handleColorSelect(color.value)}
              sx={{
                width: size - 8,
                height: size - 8,
                borderRadius: "50%",
                bgcolor: color.value,
                cursor: "pointer",
                border:
                  selectedColor === color.value
                    ? "3px solid white"
                    : "2px solid transparent",
                boxShadow:
                  selectedColor === color.value
                    ? `0 0 0 2px ${color.value}`
                    : "none",
                "&:hover": { transform: "scale(1.1)" },
                transition: "transform 0.1s",
              }}
              title={color.name}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default ColourPicker;
